import { UtilsService } from 'core/utils/utils.service';
import { Injectable } from '@angular/core';
import { ApiService } from 'core/api/api.service';
import { ConfidenceToClose } from 'src/app/modules/shared/entities/confidence-to-close.enum';
import { LeadStatus } from 'src/app/modules/shared/entities/lead-status.enum';
import { ProjectStatus } from 'src/app/modules/shared/entities/project-status.enum';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';
import { LeadData } from 'src/app/modules/shared/entities/lead-data.class';
import { ExportUtilsService } from 'core/export-utils/export-utils.service';
import { environment } from '../../../../../environments/environment';
import { ProjectDetails as ProjectData } from '../entities/projectDetails';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private urls = {
    accountManagers: '/api/accountmanager/list',
    withUnassigned: '?includeUnassigned=true',
    excludeTerm: '&excludeTerminated=true',
    errorList: '/api/client/errorList',
    syncHistory: '/api/client/syncHistory',
    openOrders: '/api/ln-order/open-orders/',
    shippedOrders: '/api/ln-order/shipped-orders/',
    templateValues: '/api/dashboard/templateValues',
    getLeads: '/api/followup/lead/list',
    updateLeadOwner: '/api/followup/lead/updateLeadOwner',
    updateStatus: '/api/followup/lead/updateStatus',
    getLead: '/api/followup/lead/get/',
    saveLead: '/api/followup/lead/save',
    downloadExcel: '/api/followup/lead/download',
    uploadExcel: '/api/followup/lead/import',
    getProjects: '/api/followup/project/list',
    getProject: '/api/followup/project/get/',
    createProject: '/api/followup/project/create',
    saveProject: '/api/followup/project/save',
    saveNote: '/api/followup/project/saveNote',
    deleteNote: '/api/followup/project/deleteNote'
  };

  errorListResult = {
    countErrors: '',
    errors: []
  };

  adminLeadsStatus = [LeadStatus.CustomerContacted, LeadStatus.CustomerInterested, LeadStatus.QuoteCreated, LeadStatus.RejectLead];
  amLeadsStatus = [LeadStatus.CustomerContacted, LeadStatus.CustomerInterested, LeadStatus.QuoteCreated];
  historyLeadsStatus = [LeadStatus.CustomerSubmittedPO, LeadStatus.DeadEnd, LeadStatus.ConvertedToCustomer];
  leadsStatusNeedingNote = [LeadStatus.DeadEnd, LeadStatus.RejectLead];

  projectsStatus = [ProjectStatus.Prospect, ProjectStatus.Quote, ProjectStatus.Imported];
  historyProjectsStatus = [ProjectStatus.Cancelled, ProjectStatus.POReceived];

  cachedTemplateValues: any;
  cachedAccountManagers: any;
  cachedLeadsAccountManagers: any;

  constructor(
    private apiService: ApiService,
    private exportUtilsService: ExportUtilsService,
    private utilsService: UtilsService,
  ) {
  }

  getAccountManagers(callback: any, errorCallback?: () => void) {
    if (this.cachedAccountManagers) {
      callback(this.cachedAccountManagers);
    } else {
      this.apiService.get(this.urls.accountManagers).subscribe({
        next: returnValues => {
          this.cachedAccountManagers = returnValues;
          callback(returnValues);
        },
        error: () => errorCallback?.()
      });
    }
  }

  getFollowupAccountManagers(callback: any) {
    if (this.cachedLeadsAccountManagers) {
      callback(this.cachedLeadsAccountManagers);
    } else {
      this.apiService.get(this.urls.accountManagers + this.urls.withUnassigned + this.urls.excludeTerm).subscribe(returnValues => {
        this.cachedLeadsAccountManagers = returnValues;
        callback(returnValues);
      });
    }
  }

  getErrorList(pageIndex, offsetPage, callback: (...args: any[]) => any) {
    const payload = {
      page: pageIndex + 1,
      offset: offsetPage
    };
    this.apiService.postGeneric(environment.offlineApiRoot + this.urls.errorList, payload, true).subscribe(result => callback(result));
  }

  getSyncHistory(pageIndex, offsetPage, callback: (...args: any[]) => any) {
    const payload = {
      page: pageIndex + 1,
      offset: offsetPage
    };
    this.apiService.postGeneric(environment.offlineApiRoot + this.urls.syncHistory, payload, true).subscribe(result => callback(result));
  }

  /**
   * Getting LN open orders for a specific account manager
   * @param accountManagerId account manager id
   */
  getLNOpenOrders(accountManagerId: string) {
    return this.apiService.get(this.urls.openOrders + accountManagerId);
  }

  /**
   * Getting LN shipped orders for a specific account manager
   * @param accountManagerId account manager id
   */
  getLNShippedOrders(accountManagerId: string) {
    return this.apiService.get(this.urls.shippedOrders + accountManagerId);
  }

  /**
   * Getting static values
   */
  getStaticValues(callback: any) {
    if (this.cachedTemplateValues) {
      callback(this.cachedTemplateValues);
    } else {
      this.apiService.get(this.urls.templateValues).subscribe(returnValues => {
        this.cachedTemplateValues = returnValues;
        callback(returnValues);
      });
    }
  }

  /**
   * Getting leads list for a specific account manager
   * @param accountManagerId account manager id
   */
  getLeads(accountManagerId: string, offset: number, pageSize: number, sort: any, isHistory: boolean) {
    const cachedSearchValues = JSON.parse(sessionStorage.getItem(StorageValues.CachedFilters +
      (isHistory ? StorageValues.LeadsHistoryKey : StorageValues.LeadsKey)));

    let nameFilter = null;
    if (this.utilsService.hasAnyValue(cachedSearchValues)) {
      nameFilter = {
        leadOwnerId: cachedSearchValues?.leadOwnerId ?? null,
        company: cachedSearchValues?.companyName ?? null,
        statusId: cachedSearchValues?.statusId ?? null,
        stateId: cachedSearchValues?.stateId ?? null,
        stateName: cachedSearchValues?.stateName ?? null,
        countryId: cachedSearchValues?.countryId ?? null,
        city: cachedSearchValues?.city ?? null,
      };
    }

    const payload = {
      accountManagerId,
      offset,
      pageSize,
      isHistory,
      sort,
      nameFilter
    };
    return this.apiService.post(this.urls.getLeads, payload);
  }

  updateLeadOwner(leadId: string, leadOwnerId: string) {
    const payload = {
      leadId,
      leadOwnerId
    };
    return this.apiService.post(this.urls.updateLeadOwner, payload);
  }

  updateStatus(leadId: string, note: string, status: number) {
    const payload = {
      leadId,
      note,
      status
    };
    return this.apiService.post(this.urls.updateStatus, payload);
  }

  getLeadInfo(leadId: string, contactId: string) {
    return this.apiService.get(this.urls.getLead + leadId + '/' + contactId);
  }

  saveLeadInfo(payload: LeadData) {
    return this.apiService.post(this.urls.saveLead, payload);
  }

  downloadExcelTemplate() {
    this.apiService.getBlob(this.urls.downloadExcel)
      .subscribe((data: ArrayBuffer) => this.exportUtilsService.downloadFile(data, 'LEADS.xlsx'));
  }

  uploadExcel(fileList: FileList, callback: (...args: any[]) => any) {
    const file: File = fileList[0];
    const formData: FormData = new FormData();
    formData.append('LeadsUploadTemplate', file, file.name);
    return this.apiService.post(this.urls.uploadExcel, formData)
      .subscribe(() => callback(), error => callback(error));
  }

  getProjects(accountManagerId: string, offset: number, pageSize: number, sort: any, isHistory: boolean) {
    const cachedSearchValues = JSON.parse(sessionStorage.getItem(StorageValues.CachedFilters +
      (isHistory ? StorageValues.ProjectsHistoryKey : StorageValues.ProjectsKey)));

    let nameFilter = null;
    if (this.utilsService.hasAnyValue(cachedSearchValues)) {
      nameFilter = {
        projectOwnerId: cachedSearchValues?.projectOwnerId ?? null,
        companyName: cachedSearchValues?.companyName ?? null,
        projectName: cachedSearchValues?.projectName ?? null,
        contactName: cachedSearchValues?.contactName ?? null,
        statusId: cachedSearchValues?.statusId ?? null
      };
    }

    const payload = {
      accountManagerId,
      offset,
      pageSize,
      isHistory,
      sort,
      nameFilter
    };
    return this.apiService.post(this.urls.getProjects, payload);
  }

  getProjectInfo(projectId: string) {
    return this.apiService.get(this.urls.getProject + projectId);
  }

  createProject(payload: ProjectData) {
    payload.confidenceToCloseId = this.getConfidenceToCloseId(payload.confidenceToClose);
    payload.estimatedSalesYear = payload.estimatedSalesYear.toString();
    payload.techResourcesNeeded = payload.techResourcesNeeded ?? false;
    delete payload.projectId;
    return this.apiService.post(this.urls.createProject, payload);
  }

  saveProjectInfo(payload: ProjectData) {
    payload.confidenceToCloseId = this.getConfidenceToCloseId(payload.confidenceToClose);
    payload.estimatedSalesYear = payload.estimatedSalesYear.toString();
    return this.apiService.post(this.urls.saveProject, payload);
  }

  deleteProjectNote(projectNoteId: string) {
    return this.apiService.post(this.urls.deleteNote, { projectNoteId });
  }

  addProjectNote(projectId: string, noteText: string) {
    return this.apiService.post(this.urls.saveNote, { projectId, noteText });
  }

  private getConfidenceToCloseId(confidenceToClose: string) {
    return Number(Object.keys(ConfidenceToClose).find(
      key => ConfidenceToClose[key] === confidenceToClose));
  }
}
